import { FC } from 'react';
import { useBlocker } from 'react-router-dom';
import Button from '../../Button/Button';
import { ButtonVariants } from '../../Button/Button.types';
import Modal from '../Modal';
import styles from './UnsavedChangesModal.module.css';
import { UnsavedChangesModalProps } from './UnsavedChangesModal.types';

/**
 * Displays a modal to prompt for user confirmation before routing away from forms with unsaved changes.
 * @param unsavedChangesExist boolean; The component rendering the UnsavedChangesModal should track whether unsaved changes exist internally - see EditDsc.tsx
 * @returns FC - Renders a modal component that controls its' own open/closed state
 */
const UnsavedChangesModal: FC<UnsavedChangesModalProps> = ({ unsavedChangesExist }) => {
  // Ask the router to block navigation when there are unsaved changes.
  // Tests mock this hook, so calling it directly is important.
  const blocker = useBlocker?.(Boolean(unsavedChangesExist));

  const isOpen = Boolean(unsavedChangesExist) && blocker?.state === 'blocked';

  const handleCancel = () => {
    // Safely reset the blocker if available; noop if not provided
    blocker?.reset?.();
  };

  const handleProceed = () => {
    // Safely proceed if available; noop if not provided
    blocker?.proceed?.();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      headerContent={'You have unsaved changes, are you sure you want to leave?'}
      bodyContent={null}
      footerContent={
        <div className={styles.footerButtons}>
          <Button variant={ButtonVariants.Secondary} onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant={ButtonVariants.Primary} onClick={handleProceed}>
            Leave without saving
          </Button>
        </div>
      }
    />
  );
};

export default UnsavedChangesModal;
